import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { SportOrShared } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconClock } from '@sorare/core/src/atoms/icons/system/iconClock.ts';
import { iconGift } from '@sorare/core/src/atoms/icons/system/iconGift.ts';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelS } from '@sorare/core/src/atoms/typography';
import { Bold } from '@sorare/core/src/atoms/typography/Bold';
import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useGenerateDialogPath } from '@sorare/core/src/hooks/navigation/useGenerateDialogPath';
import { useTimeLeft } from '@sorare/core/src/hooks/useTimeLeft';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import { glossary } from '@sorare/core/src/lib/glossary';

import { DumbNotification } from '#src/components/activity/DumbNotification/index.tsx';
import { commonNotificationInterfaceFragment } from '#src/components/activity/Notification/fragments.ts';
import type { CommonNotificationProps } from '#src/components/activity/Notification/types.ts';

import type { LoanAvailableNotification_loanNotification } from './__generated__/index.graphql.ts';

type Props = CommonNotificationProps & {
  notification: LoanAvailableNotification_loanNotification;
};
const StyledButton = styled(Button)`
  margin: var(--unit) 0;
`;

export const LoanAvailableNotification = ({ notification, ...rest }: Props) => {
  const track = useEvents();
  const { currentUser } = useCurrentUserContext();
  const { message, isEnded } = useTimeLeft(notification.eligibleUntil);
  const generateDialogPath = useGenerateDialogPath();

  const { name, createdAt, sport, read } = notification;

  let link: string | undefined = generateDialogPath(
    DialogKey.loanWelcomeScreen,
    '1'
  );
  let ctaMsg = glossary.claim;

  if (isEnded) {
    ctaMsg = glossary.expired;
    link = undefined;
  }
  if (currentUser?.loan) {
    ctaMsg = glossary.claimed;
    link = undefined;
  }
  return (
    <DumbNotification
      name={name}
      title={
        <Vertical as="span">
          {!currentUser?.loan && (
            <Horizontal as="span">
              <FontAwesomeIcon size="xs" icon={iconClock} />
              <LabelS bold>
                {isEnded ? (
                  <FormattedMessage {...glossary.expired} />
                ) : (
                  <FormattedMessage
                    defaultMessage="Expires in {value}"
                    values={{ value: message }}
                  />
                )}
              </LabelS>
            </Horizontal>
          )}
          <span>
            <FormattedMessage
              defaultMessage="<strong>Try the Sorare experience!</strong> Loan your first lineup"
              values={{ strong: Bold }}
            />
          </span>
        </Vertical>
      }
      icon={iconGift}
      sport={sport}
      createdAt={createdAt}
      link={link}
      read={read}
      {...rest}
    >
      {!isEnded && (
        <StyledButton
          disabled={!link}
          size="small"
          asSpan
          onClick={() => {
            track('Click Loan Notification', {
              sport: (sport !== SportOrShared.GLOBAL && sport) || undefined,
            });
          }}
        >
          <FormattedMessage {...ctaMsg} />
        </StyledButton>
      )}
    </DumbNotification>
  );
};

LoanAvailableNotification.fragments = {
  loanNotification: gql`
    fragment LoanAvailableNotification_loanNotification on LoanNotification {
      id
      eligibleUntil
      ...Notification_notificationInterface
    }
    ${commonNotificationInterfaceFragment}
  ` as TypedDocumentNode<LoanAvailableNotification_loanNotification>,
};
